import Cookies from 'js-cookie';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { defaultBirthDate } from '~constants/forms';
import type { TGender } from '~constants/gender';
import {
	RegisterInbound,
	RegisterOutbound,
} from '~features/annals/enums/register';
import type { RegisterAnalytics } from '~features/annals/types';
import { updateUserRegistrationInfo } from '~features/annals/updateUserRegistrationInfo';
import { getLocale } from '~features/language/model/utils';
import { localeCookieName, localeExpires } from '~i18n/constants';
import { authApi } from '~services/api/auth';
import { locationApi } from '~services/api/location';
import { userApi } from '~services/api/user';
import { MarkersService } from '~services/markers';
import { api, apiTags } from '~shared/api/origin';
import type { AppDispatch } from '~store/index';
import { getHost } from '~utils/host';

type UserUpdateData = {
	name: string;
	id: string;
	gender: TGender;
};

type EmailRegisterParams = UserUpdateData & {
	email: string;
	password: string;
	analytics: RegisterAnalytics;
};

type GoogleRegisterParams = UserUpdateData & {
	token: string;
	analytics: RegisterAnalytics;
};

type DiscordRegisterParams = {
	token: string;
	analytics: RegisterAnalytics;
	id: string;
};

export const useRegister = () => {
	const dispatch = useDispatch<AppDispatch>();

	const [send] = authApi.useRegisterMutation();
	const [sendGoogle] = authApi.useRegisterGoogleMutation();
	const [sendDiscord] = authApi.useRegisterDiscordMutation();
	const [sendEdit] = userApi.useEditUserInfoMutation();
	const [sendPreferences] = userApi.useSetUserDefaultPreferencesMutation();
	const [setUserLanguage] = userApi.useSetUserLanguageMutation();
	const [sendSelectedBot] = userApi.useSetSelectedBotMutation();
	const [getLocation] = locationApi.useLazyGetLocationQuery();

	const postRegister = useCallback(() => {
		MarkersService.clearMarkers();
		//tags are re-requested after register automatically
		//however, we have to re-request them due to our backend may be slow in terms of adding tags to user
		dispatch(api.util.invalidateTags([apiTags.tags]));
	}, [dispatch]);

	const setUserRegisterData = useCallback(
		async ({
			name,
			gender,
			id,
			analytics,
		}: UserUpdateData & { analytics: RegisterAnalytics }) => {
			// fire this asap after actual register
			const { origin, pathname, search, hash } = window.location;
			const { data } = await getLocation({});
			const markers = MarkersService.getMarkersRegistration();
			await updateUserRegistrationInfo({
				userId: id,
				data: {
					...markers,
					location: data?.country ?? 'N/A',
					register_in: analytics?.inbound || RegisterInbound.NoneIdentified,
					register_out: analytics?.outbound || RegisterOutbound.NoneIdentified,
					link: `${origin}${pathname}`,
					link_full: `${origin}${pathname}${search}${hash}`,
				},
			});

			const locale = getLocale(window.location.host);

			Cookies.set(localeCookieName, locale, {
				expires: localeExpires,
				domain: getHost(),
			});

			//fire rest in parallel
			return Promise.all([
				setUserLanguage({ userId: id, language: locale }).unwrap(),
				sendSelectedBot({ id }).unwrap(),
				sendPreferences({ id }).unwrap(),
				sendEdit({
					id,
					data: {
						name,
						gender,
						birthday: defaultBirthDate,
					},
				}).unwrap(),
			]);
		},
		[getLocation, sendEdit, sendPreferences, sendSelectedBot, setUserLanguage]
	);

	const registerViaEmail = useCallback(
		async ({
			email,
			password,
			name,
			gender,
			id,
			analytics,
		}: EmailRegisterParams) => {
			await send({
				email,
				password,
				analytics,
			}).unwrap();
			await setUserRegisterData({
				id,
				name,
				gender,
				analytics,
			});
			postRegister();
		},
		[postRegister, send, setUserRegisterData]
	);

	const registerViaGoogle = useCallback(
		async ({ token, name, gender, id, analytics }: GoogleRegisterParams) => {
			await sendGoogle({
				id,
				token,
				analytics,
			}).unwrap();
			await setUserRegisterData({
				id,
				name,
				gender,
				analytics,
			});
			postRegister();
		},
		[postRegister, sendGoogle, setUserRegisterData]
	);

	const registerViaDiscord = useCallback(
		async ({ token, id, analytics }: DiscordRegisterParams) => {
			await sendDiscord({
				id,
				token,
				analytics,
			}).unwrap();

			const promises = [];

			promises.push(
				sendEdit({
					id,
					data: {
						name: 'Me',
						gender: 'mal',
						birthday: defaultBirthDate,
					},
				}).unwrap()
			);

			promises.push(
				setUserRegisterData({
					id,
					name: 'Me',
					gender: 'mal',
					analytics,
				})
			);
			await Promise.all(promises);
			postRegister();
		},
		[postRegister, sendDiscord, sendEdit, setUserRegisterData]
	);

	return {
		registerViaEmail,
		registerViaGoogle,
		registerViaDiscord,
	};
};
